import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { EmployeeRequestApprovalStatusDialogComponent } from 'src/app/components/main/shared/employee-request-approval-status-dialog/employee-request-approval-status-dialog.component';
import {
  EmployeeRequestResponsibleForAnswerStatusEnum,
  EmployeeRequestResponsibleForAnswerStatusLabels,
} from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import { EmployeeRequestResponsibleForAnswersRequestableTypeEnum } from 'src/app/enums/employee-request-responsible-for-answers-requestable-type.enum';
import { EmployeeRequestApprovalStatusDialogData } from 'src/app/models/employee-request-responsible-for-answer.model';
import { WorkingExtraHoursPeriod } from 'src/app/models/working-extra-hours-period.model';
import { ApiService } from 'src/app/services';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UserService } from 'src/app/services/user.service';
import { UtilsService } from 'src/app/services/utils.service';
import { CompanyProduct } from 'src/app/models/company_product.model';

@Component({
  selector: 'app-working-extra-hours-periods-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    DatePipe,
  ],
  templateUrl: './working-extra-hours-periods-management.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./working-extra-hours-periods-management.component.css'],
})
export class WorkingExtraHoursPeriodsManagementComponent extends IndexTemplateComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  protected fb: UntypedFormBuilder;
  protected router: Router;
  protected activatedRoute: ActivatedRoute;
  protected utils: UtilsService;

  readonly EmployeeRequestResponsibleForAnswerStatusEnum = EmployeeRequestResponsibleForAnswerStatusEnum;

  readonly displayedColumns: string[] = [
    'user',
    'start_date',
    'end_date',
    'max_weekly_hours',
    'company_product',
    'justification',
    'status',
    'actions',
  ];

  readonly dataSource = new MatTableDataSource<WorkingExtraHoursPeriod>([]);

  private readonly api = inject(ApiService);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly permissions = inject(PermissionsService);
  private readonly user = inject(UserService);

  protected readonly currentUserId = this.user.getCurrentUser()?.id;
  protected readonly is_rrhh_or_admin = toSignal(this.permissions.validateDepartmentObservable(['direction', 'rrhh']), {
    initialValue: false,
  });

  override filterFormExtraParams!: {
    status: EmployeeRequestResponsibleForAnswerStatusEnum | 'ignored' | '';
    responsible_user_id: number | '';
  };

  constructor() {
    const fb = inject(UntypedFormBuilder);
    const router = inject(Router);
    const activatedRoute = inject(ActivatedRoute);
    const utils = inject(UtilsService);

    super(router, fb, activatedRoute, utils);

    this.fb = fb;
    this.router = router;
    this.activatedRoute = activatedRoute;
    this.utils = utils;

    const statusFromQuery = activatedRoute.snapshot.queryParamMap.get('status');

    const responsibleUserIdFromQuery = activatedRoute.snapshot.queryParamMap.get('responsible_user_id');

    const responsible_user_id =
      responsibleUserIdFromQuery === null
        ? (this.currentUserId ?? '')
        : responsibleUserIdFromQuery === ''
          ? ''
          : Number(responsibleUserIdFromQuery);

    this.filterFormExtraParams = {
      status:
        (statusFromQuery as EmployeeRequestResponsibleForAnswerStatusEnum | 'ignored' | null) ??
        EmployeeRequestResponsibleForAnswerStatusEnum.PENDING,
      responsible_user_id: responsible_user_id,
    };

    if (responsibleUserIdFromQuery === null && this.currentUserId !== undefined) {
      void router.navigate([], {
        relativeTo: activatedRoute,
        queryParams: {
          responsible_user_id: this.currentUserId,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  override fetchData(): void {
    const params = {
      ...this.filterForm.value,
      with: 'user,company_products',
      order_by: 'created_at:desc',
    };

    if (!params.status || params.status === 'ignored') {
      delete params.status;
    }

    this.api
      .getWorkingExtraHoursPeriods(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: data => {
          this.dataSource.data = data.data;

          this.setMetadata(Number(data.meta.total), Number(data.meta.current_page) - 1, Number(data.meta.per_page));
        },
      });
  }

  accept(id: number): void {
    if (!window.confirm('¿Aceptar solicitud?')) {
      return;
    }

    this.api
      .updateWorkingExtraHoursPeriod(id, {
        status: EmployeeRequestResponsibleForAnswerStatusEnum.ACCEPTED,
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.fetchData();
        },
      });
  }

  reject(id: number): void {
    if (!window.confirm('¿Rechazar solicitud?')) {
      return;
    }

    this.api
      .updateWorkingExtraHoursPeriod(id, {
        status: EmployeeRequestResponsibleForAnswerStatusEnum.REJECTED,
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.fetchData();
        },
      });
  }

  getStatusLabel(status: EmployeeRequestResponsibleForAnswerStatusEnum): string {
    return EmployeeRequestResponsibleForAnswerStatusLabels[status];
  }

  formatCompanyProducts(working_extra_hours_period: WorkingExtraHoursPeriod): string {
    const company_product_titles =
      working_extra_hours_period.company_products?.map((company_product: CompanyProduct): string => company_product.title) ?? [];

    return company_product_titles.length > 0 ? company_product_titles.join(', ') : '—';
  }

  openApprovalStatusDialog(working_extra_hours_period: WorkingExtraHoursPeriod): void {
    this.dialog.open(EmployeeRequestApprovalStatusDialogComponent, {
      width: '800px',
      data: {
        requestable_type: EmployeeRequestResponsibleForAnswersRequestableTypeEnum.WorkingExtraHoursPeriod,
        requestable_id: working_extra_hours_period.id,
        requestable_status: working_extra_hours_period.status,
      } as EmployeeRequestApprovalStatusDialogData,
    });
  }
}
